import type { ReactNode } from 'react';
import type { GridApi } from '@ag-grid-community/core';
import { EmptyState, type EmptyStateProps } from '@ics-portal/guardian';

export interface CustomEmptyStateProps<TData = any> extends EmptyStateProps {
    api: GridApi<TData>;
}

/**
 * A custom component for replacing the default NoRowsOverlay component for AG Grid.
 * @remarks
 * This component is used to display a custom empty state message when there are no rows to display in the grid.
 * If the grid has an active filter of any kind and there are no rows to display, a different predefined message will be shown.
 *
 * See {@link https://www.ag-grid.com/react-data-grid/overlays/#custom-no-rows-overlay} for details.
 */
function CustomEmptyState<TData = any>(
    props: CustomEmptyStateProps<TData>,
): ReactNode {
    const { api, ...emptyStateProps } = props;
    const isFiltering = api.isAnyFilterPresent();
    const totalRowCount = api.getDisplayedRowCount();

    function handleOnClearFilters(): void {
        api.setFilterModel(null);
    }

    /*
     * Display a more relevant message when the grid is filtered and there are no rows to display.
     */
    if (isFiltering && totalRowCount === 0) {
        return (
            <EmptyState
                title="No results available"
                description="Try broadening your search or adjusting your filters."
                buttonProps={{
                    label: 'Clear all',
                    onClick: handleOnClearFilters,
                }}
            />
        );
    }

    return <EmptyState {...emptyStateProps} />;
}

export default CustomEmptyState;
